import type { TextLinkProps } from '@time-line/components/common';
import { Button, TextLink } from '@time-line/components/common';
import { useHcpLinkTracking } from 'hooks/useHcpLinkTracking';
import { ACCOUNT } from 'lib/constants/routes';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import type { ReactNode } from 'react';
import {
  getExternalLinkProps,
  getShowIconProps,
} from 'utils/getExternalLinkProps';
import { getTrackingId } from 'utils/getTrackingId';

interface HeaderNavItemProps extends TextLinkProps {
  children: ReactNode;
  href?: string;
  name: string;
  isExternal?: boolean;
  onClick?: () => void;
  showIcon?: boolean;
}

export function HeaderNavItem({
  href,
  name,
  children,
  isExternal,
  onClick,
  showIcon,
  ...props
}: HeaderNavItemProps) {
  const id = getTrackingId(name, { parent: 'header', platform: 'desktop' });
  const { status } = useSession();
  const isAuthenticated = status === 'authenticated';
  const router = useRouter();
  const trackHcpLinkClick = useHcpLinkTracking(name, { source: 'header' });

  const handleOnClick = () => {
    if (!onClick) return;

    if (name === 'account' && isAuthenticated) {
      return router.push(ACCOUNT);
    }

    return onClick();
  };

  if (href) {
    const isCurrentPage = router.asPath.split(/[?#]/)[0] === href;

    return (
      <TextLink
        className="header_nav-item"
        href={href}
        aria-current={isCurrentPage ? 'page' : undefined}
        {...getExternalLinkProps(isExternal, href)}
        {...getShowIconProps(showIcon)}
        textProps={{ size: 'overline' }}
        variant="underlineOnHover"
        id={id}
        onClick={trackHcpLinkClick}
        afterIconProps={
          showIcon ? { boxSize: '2xs', marginBottom: '2px' } : undefined
        }
        {...props}
      >
        {children}
      </TextLink>
    );
  }

  if (onClick) {
    return (
      <Button
        className="header_nav-item"
        onClick={handleOnClick}
        variant="link"
        textDecoration="none"
        textUnderlineOffset={2}
        _hover={{ textDecoration: 'underline' }}
      >
        {children}
      </Button>
    );
  }

  return null;
}
